import React, { useCallback, useMemo } from "react";
import clsx from "clsx";

import { Table, Tooltip } from "components";
import { EditableFieldWithHookSelect } from "components/hookForms";
import { SelectProps } from "components/select";
import { matchReportColumnType } from "types";

import { ReactComponent as ShowIcon } from "static/images/eye.svg";
import styles from "./reports.module.scss";

type EditableFieldsProps = {
	submitHandler: Function;
	isLoading?: boolean;
	reactSelectProps?: SelectProps;
};

type ReportTableProps = {
	data: matchReportColumnType[];
	isRerun?: boolean;
	disableHeader?: boolean;
	bordered?: "horizontal";
	isLoading?: boolean;
	editableFields?: { [key: string]: EditableFieldsProps };
	alignStart?: boolean;
	isOriginalHidden?: boolean;
};

const ReportTable = ({
	data = [],
	isRerun = false,
	disableHeader = false,
	bordered,
	isLoading = false,
	editableFields = {},
	alignStart = false,
	isOriginalHidden,
}: ReportTableProps) => {
	const DisplayValue = useCallback(({ value, className }) => {
		return value ? (
			<span>
				<span className={className}>{value}</span>
			</span>
		) : null;
	}, []);

	const columns = useMemo(
		() => [
			{
				Header: "",
				accessor: "name",
				disableSortBy: true,
				classNameHeader: styles.reportHeader,
				classNameCell: clsx(
					"text-muted",
					styles.reportHeader,
					alignStart && "align-items-start",
				),
			},
			...(isOriginalHidden
				? []
				: [
						{
							Header: `${isRerun ? "Original Inquiry" : ""}`,
							accessor: "originalInquiry",
							disableSortBy: true,
							Cell({ value, row: { original } }) {
								const className =
									original.isMatch || !isRerun
										? ""
										: styles.highlight;
								return editableFields?.hasOwnProperty(
									original.fieldName,
								) ? (
									isRerun ? (
										<DisplayValue
											className={className}
											value={value.data}
										/>
									) : (
										<EditableFieldWithHookSelect
											reactSelectProps={{
												name: original.fieldName,
												options: original.options,
												getOptionLabel: option =>
													option.displayName,
												getOptionValue: option =>
													option.name,
												...editableFields[
													original.fieldName
												]?.reactSelectProps,
												menuPortalTarget: document.body,
											}}
											defaultValue={value.data}
											getSelectedValue={(
												option,
												defaultValue,
											) =>
												option.displayName ===
												defaultValue
											}
											onSubmit={
												editableFields[
													original.fieldName
												]?.submitHandler
											}
											isLoading={
												editableFields[
													original.fieldName
												]?.isLoading
											}
										/>
									)
								) : (
									<>
										<DisplayValue
											className={className}
											value={value.data}
										/>
										{value.tooltip && (
											<Tooltip title={value.tooltip}>
												<ShowIcon
													className={styles.showIcon}
												/>
											</Tooltip>
										)}
									</>
								);
							},
						},
				  ]),
			...(isRerun
				? [
						{
							Header: "Re-run Inquiry",
							accessor: "reRunInquiry",
							disableSortBy: true,
							classNameCell: styles.border,

							Cell({ value, row: { original } }) {
								const className =
									original.isMatch || isOriginalHidden
										? ""
										: styles.highlight;
								return editableFields?.hasOwnProperty(
									original.fieldName,
								) ? (
									<EditableFieldWithHookSelect
										reactSelectProps={{
											name: original.fieldName,
											options: original.options,
											getOptionLabel: option =>
												option.displayName,
											getOptionValue: option =>
												option.value,
											...editableFields[
												original.fieldName
											]?.reactSelectProps,
											menuPortalTarget: document.body,
										}}
										defaultValue={value.data}
										getSelectedValue={(
											option,
											defaultValue,
										) =>
											option.displayName === defaultValue
										}
										onSubmit={
											editableFields[original.fieldName]
												?.submitHandler
										}
										isLoading={
											editableFields[original.fieldName]
												?.isLoading
										}
									/>
								) : (
									<>
										<DisplayValue
											className={className}
											value={value.data}
										/>
										{value.tooltip && (
											<Tooltip title={value?.tooltip}>
												<ShowIcon
													className={styles.showIcon}
												/>
											</Tooltip>
										)}
									</>
								);
							},
						},
				  ]
				: []),
		],
		[DisplayValue, alignStart, editableFields, isOriginalHidden, isRerun],
	);

	return (
		<Table
			disableHeader={disableHeader}
			columns={columns}
			data={data}
			bordered={bordered}
			hideFooter
			meta={{ loading: isLoading, totalItems: 0 }}
		/>
	);
};

export default ReportTable;
